import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, updateDoc, doc } from 'firebase/firestore';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { FaStar } from 'react-icons/fa';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';
import { resolveClientLifecycle, getProgramTier, isActiveClientAccount, programMapWith } from '../utils/accountLifecycle';

const colors = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const LostClientsReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [activeTab, setActiveTab] = useState('Lost');
  const [loading, setLoading] = useState(true);
  const [allClients, setAllClients] = useState([]);
  const [timeFilter, setTimeFilter] = useState('This Year');
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editClient, setEditClient] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editStageBeforeLost, setEditStageBeforeLost] = useState('');
  const [editSource, setEditSource] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPostMortem, setEditPostMortem] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const accountsRef = collection(db, 'accounts');
        // Query by type only to avoid composite index issues; filter Lost client-side
        // Try 'Clients' first
        let data = [];
        try {
          const q = query(accountsRef, where('type', '==', 'Clients'), orderBy('createdAt', 'desc'));
          const snapshot = await getDocs(q);
          data = snapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        } catch (err) {
          console.warn('Failed to query with orderBy, trying without:', err);
          // Fallback: query without orderBy
          const q = query(accountsRef, where('type', '==', 'Clients'));
          const snapshot = await getDocs(q);
          data = snapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        }
        
        // Also check for lowercase 'clients' type and merge
        let lowercaseData = [];
        try {
          const lowercaseQ = query(accountsRef, where('type', '==', 'clients'), orderBy('createdAt', 'desc'));
          const lowercaseSnapshot = await getDocs(lowercaseQ);
          lowercaseData = lowercaseSnapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        } catch (err) {
          console.warn('Failed to query lowercase clients with orderBy, trying without:', err);
          // Fallback: query without orderBy
          const lowercaseQ = query(accountsRef, where('type', '==', 'clients'));
          const lowercaseSnapshot = await getDocs(lowercaseQ);
          lowercaseData = lowercaseSnapshot.docs.map(doc => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt?.toDate?.() || (d.createdAt instanceof Date ? d.createdAt : null),
              lostAt: d.lostAt?.toDate?.() || (d.lostAt instanceof Date ? d.lostAt : null),
              lastActivityAt: d.lastActivityAt?.toDate?.() || (d.lastActivityAt instanceof Date ? d.lastActivityAt : null),
              program: d.program ? {
                ...programMapWith(d.program),
                startDate: d.program.startDate?.toDate?.() || (d.program.startDate instanceof Date ? d.program.startDate : d.program.startDate),
                endDate: d.program.endDate?.toDate?.() || (d.program.endDate instanceof Date ? d.program.endDate : d.program.endDate),
              } : d.program,
            };
          });
        }
        
        const mergedClients = [...data, ...lowercaseData];
        // Remove duplicates based on id
        const uniqueClients = Array.from(new Map(mergedClients.map(item => [item.id, item])).values());
        // Sort by createdAt descending
        const sortedClients = uniqueClients.sort((a, b) => {
          const aDate = a.createdAt || new Date(0);
          const bDate = b.createdAt || new Date(0);
          return bDate - aDate;
        });
        setAllClients(sortedClients);
      } catch (err) {
        console.error('Failed to fetch clients', err);
        setAllClients([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const filterByTime = (items, dateField) => {
    if (!items?.length) return [];
    if (timeFilter === 'All Time') return items;
    const now = new Date();
    const start = new Date();
    if (timeFilter === 'This Year') {
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      start.setMonth(q * 3, 1);
      start.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'This Month') {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
    }
    return items.filter(it => {
      const dt = it[dateField];
      return dt instanceof Date && dt >= start;
    });
  };

  // Filter clients based on active tab
  const clients = useMemo(() => {
    // One lifecycle rule (accountLifecycle.js): Lost / Canceled / Archived — stored, or derived
    // from a cancel date that has passed — count as lost here; everything else is active.
    if (activeTab === 'Lost') {
      return allClients.filter((c) => resolveClientLifecycle(c).isEnded);
    }
    if (activeTab === 'Active') {
      return allClients.filter((c) => isActiveClientAccount(c));
    }
    return allClients;
  }, [allClients, activeTab]);

  const filteredClients = useMemo(() => {
    // Lost tab: filter by when the client actually ended (lostAt / canceledAt / archivedAt, or the
    // cancel date that passed), falling back to createdAt for docs written before those fields.
    if (activeTab === 'Lost') {
      const withEnded = clients.map((c) => ({ ...c, endedAt: resolveClientLifecycle(c).endedAt || c.lostAt || c.createdAt }));
      return filterByTime(withEnded, 'endedAt');
    }
    return filterByTime(clients, 'createdAt');
  }, [clients, timeFilter, activeTab]);

  const openEdit = (client) => {
    setEditClient(client);
    setEditReason(client.resultReason || client.substatus || client.reason || client.lostReason || '');
    setEditSource(client.source || client.channel || client.campaign || '');
    setEditCompanyName(client.company || client.accountName || client.businessName || client.organization || '');
    // Get contact name from nested contact object or fallback fields
    const contactFirst = client.contact?.firstName || '';
    const contactLast = client.contact?.lastName || '';
    const fullContactName = (contactFirst && contactLast) 
      ? `${contactFirst} ${contactLast}`.trim()
      : client.contactName || client.primaryContactName || client.contact?.name || client.name || '';
    setEditContactName(fullContactName);
    setEditEmail(client.contact?.email || client.email || '');
    setEditPhone(client.contact?.phone || client.phone || '');
    setEditNotes(client.lostNotes || client.notes || '');
    setEditPostMortem(Boolean(client.postMortem));
    const prior = client.tierBeforeLost
      || client.program?.programName
      || getProgramTier(client)
      || client.lastVisitedStage
      || client.previousStage
      || (Array.isArray(client.stageHistory) && client.stageHistory.length > 1 ? client.stageHistory[client.stageHistory.length - 2]?.stage : null)
      || client.stageBeforeLost
      || client.pipelineStage
      || '';
    setEditStageBeforeLost(prior);
    setEditError('');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editClient) return;
    setEditSaving(true);
    setEditError('');
    try {
      const ref = doc(db, 'accounts', editClient.id);
      const updateData = {
        resultReason: editReason || null,
        substatus: editReason || null,
        tierBeforeLost: editStageBeforeLost || null,
        stageBeforeLost: editStageBeforeLost || null,
        lastVisitedStage: editStageBeforeLost || null,
        previousStage: editStageBeforeLost || null,
        source: editSource || null,
        channel: editSource || null,
        company: editCompanyName || null,
        accountName: editCompanyName || null,
        businessName: editCompanyName || null,
        organization: editCompanyName || null,
        contactName: editContactName || null,
        primaryContactName: editContactName || null,
        name: editContactName || null,
        email: editEmail || null,
        phone: editPhone || null,
        lostNotes: editNotes || null,
        notes: editNotes || null,
        postMortem: !!editPostMortem,
      };
      // Update program status if program exists
      if (editClient.program) {
        updateData.program = programMapWith(editClient.program, {
          programName: editStageBeforeLost || editClient.program.programName || null,
          status: editStageBeforeLost || editClient.program.status || null,
        });
      }
      await updateDoc(ref, updateData);

      // Update local state
      setAllClients(prev => prev.map(c => c.id === editClient.id ? {
        ...c,
        resultReason: editReason || null,
        substatus: editReason || null,
        stageBeforeLost: editStageBeforeLost || null,
        lastVisitedStage: editStageBeforeLost || null,
        previousStage: editStageBeforeLost || null,
        source: editSource || null,
        channel: editSource || null,
        company: editCompanyName || null,
        accountName: editCompanyName || null,
        businessName: editCompanyName || null,
        organization: editCompanyName || null,
        contactName: editContactName || null,
        primaryContactName: editContactName || null,
        name: editContactName || null,
        email: editEmail || null,
        phone: editPhone || null,
        lostNotes: editNotes || null,
        notes: editNotes || null,
        postMortem: !!editPostMortem,
        program: c.program ? programMapWith(c.program, {
          programName: editStageBeforeLost || c.program.programName || null,
          status: editStageBeforeLost || c.program.status || null,
        }) : c.program,
      } : c));

      setEditOpen(false);
      setEditClient(null);
    } catch (e) {
      console.error(e);
      setEditError('Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  const totals = useMemo(() => {
    if (activeTab === 'Lost') {
      return {
        total: filteredClients.length,
        lost: filteredClients.length,
      };
    }
    if (activeTab === 'Active') {
      return {
        total: filteredClients.length,
        active: filteredClients.length,
      };
    }
    // For All tab
    const lost = filteredClients.filter(c => {
      const status = c.status || c.program?.clientStatus || c.stage;
      return status === 'Lost' || status === 'lost';
    }).length;
    const active = filteredClients.filter(c => {
      const status = c.status || c.program?.clientStatus || c.stage;
      return status !== 'Lost' && status !== 'lost';
    }).length;
    return {
      total: filteredClients.length,
      lost,
      active,
    };
  }, [filteredClients, activeTab]);

  const lostByReason = useMemo(() => {
    const counts = {};
    filteredClients.forEach(c => {
      const reason = c.resultReason || c.substatus || c.reason || c.lostReason || 'Unknown';
      counts[reason] = (counts[reason] || 0) + 1;
    });
    // Filter out Unknown entries if there are actual reasons, otherwise show Unknown
    const entries = Object.entries(counts);
    const hasActualReasons = entries.some(([reason]) => reason !== 'Unknown' && counts[reason] > 0);
    const filtered = hasActualReasons 
      ? entries.filter(([reason]) => reason !== 'Unknown')
      : entries;
    return filtered.map(([reason, count], idx) => ({ reason, count, color: colors[idx % colors.length] }));
  }, [filteredClients]);

  const lostByStage = useMemo(() => {
    // Client stages: Lite, Plus, Pro, Hourly, 5 Hours, 10 Hours, A La Carte
    const STAGES = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    filteredClients.forEach(c => {
      const previousStage = c.program?.programName
        || c.program?.status
        || c.lastVisitedStage
        || c.previousStage
        || (Array.isArray(c.stageHistory) && c.stageHistory.length > 1 ? c.stageHistory[c.stageHistory.length - 2]?.stage : null)
        || c.stageBeforeLost
        || c.pipelineStage
        || null;
      if (previousStage && STAGES.includes(previousStage)) {
        counts[previousStage] = (counts[previousStage] || 0) + 1;
      }
    });
    // Only return stages that have counts > 0
    return STAGES
      .filter(stage => counts[stage] > 0)
      .map((stage, idx) => ({ stage, count: counts[stage] || 0, color: colors[idx % colors.length] }));
  }, [filteredClients]);

  const longTermLostClients = useMemo(() => {
    if (!filteredClients.length) return 0;
    // Count clients with program duration >= 6 months (180 days)
    const longTermCount = filteredClients.filter(c => {
      // Use program start and end dates from program overview
      const programStart = c.program?.startDate;
      const programEnd = c.program?.endDate;
      const start = programStart 
        ? (programStart instanceof Date ? programStart : new Date(programStart))
        : null;
      const end = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : null;
      if (!start || !end || !(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) return false;
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
      return days >= 180; // 6 months or more
    }).length;
    return longTermCount;
  }, [filteredClients]);

  const avgDaysAsClient = useMemo(() => {
    if (!filteredClients.length) return 0;
    const days = filteredClients.map(c => {
      // Use program start and end dates from program overview
      const programStart = c.program?.startDate;
      const programEnd = c.program?.endDate;
      const start = programStart 
        ? (programStart instanceof Date ? programStart : new Date(programStart))
        : null;
      const end = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : null;
      if (!start || !end || !(start instanceof Date) || !(end instanceof Date) || isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
      return Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    }).filter(d => d > 0); // Only include valid day calculations
    if (days.length === 0) return 0;
    const sum = days.reduce((a, b) => a + b, 0);
    return Math.round((sum / days.length) * 10) / 10;
  }, [filteredClients]);

  const lostClientsOverTime = useMemo(() => {
    if (!filteredClients.length) return [];
    const periodCounts = {};
    const now = new Date();
    
    // Determine the start date based on time filter
    let periodStart = new Date(0); // Beginning of time for "All Time"
    if (timeFilter === 'This Year') {
      periodStart = new Date(now.getFullYear(), 0, 1);
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      periodStart = new Date(now.getFullYear(), q * 3, 1);
    } else if (timeFilter === 'This Month') {
      periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    
    filteredClients.forEach(c => {
      // Use program end date, lostAt, or lastActivityAt to determine when they were lost
      const programEnd = c.program?.endDate;
      const lostDate = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : (c.lostAt instanceof Date ? c.lostAt : (c.lostAt?.toDate ? c.lostAt.toDate() : null)) 
          || (c.lastActivityAt instanceof Date ? c.lastActivityAt : (c.lastActivityAt?.toDate ? c.lastActivityAt.toDate() : null));
      
      if (lostDate && lostDate instanceof Date && !isNaN(lostDate.getTime())) {
        let periodKey;
        let label;
        let dateForSort;
        
        if (timeFilter === 'All Time') {
          // Group by year
          const year = lostDate.getFullYear();
          periodKey = `${year}`;
          label = year.toString();
          dateForSort = new Date(year, 0, 1);
        } else if (timeFilter === 'This Year') {
          // Group by month
          const month = lostDate.getMonth();
          periodKey = `${lostDate.getFullYear()}-${String(month + 1).padStart(2, '0')}`;
          label = lostDate.toLocaleDateString('en-US', { month: 'short' });
          dateForSort = new Date(lostDate.getFullYear(), month, 1);
        } else if (timeFilter === 'This Quarter') {
          // Group by month in quarter
          const month = lostDate.getMonth();
          periodKey = `${lostDate.getFullYear()}-${String(month + 1).padStart(2, '0')}`;
          label = lostDate.toLocaleDateString('en-US', { month: 'short' });
          dateForSort = new Date(lostDate.getFullYear(), month, 1);
        } else if (timeFilter === 'This Month') {
          // Group by week
          const weekStart = new Date(lostDate);
          weekStart.setDate(lostDate.getDate() - lostDate.getDay()); // Start of week (Sunday)
          weekStart.setHours(0, 0, 0, 0);
          // Calculate week number within the month (1st week, 2nd week, etc.)
          const firstDayOfMonth = new Date(lostDate.getFullYear(), lostDate.getMonth(), 1);
          const firstWeekStart = new Date(firstDayOfMonth);
          firstWeekStart.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());
          const weekNum = Math.floor((weekStart - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
          periodKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
          label = `Week ${weekNum} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
          dateForSort = new Date(weekStart);
        }
        
        if (periodKey && (!periodStart || lostDate >= periodStart)) {
          if (!periodCounts[periodKey]) {
            periodCounts[periodKey] = { count: 0, label, dateForSort };
          }
          periodCounts[periodKey].count++;
        }
      }
    });
    
    // Convert to array and sort by date
    const sorted = Object.entries(periodCounts)
      .map(([key, { count, label, dateForSort }]) => ({
        period: label,
        date: dateForSort,
        count
      }))
      .sort((a, b) => a.date - b.date);
    
    // For "This Year" and "This Quarter", ensure all months are included even if count is 0
    if (timeFilter === 'This Year' || timeFilter === 'This Quarter') {
      const startDate = timeFilter === 'This Year' 
        ? new Date(now.getFullYear(), 0, 1)
        : new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      const endDate = now;
      const months = [];
      const current = new Date(startDate);
      
      while (current <= endDate) {
        const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        const existing = sorted.find(s => s.date.getTime() === current.getTime());
        months.push({
          period: current.toLocaleDateString('en-US', { month: 'short' }),
          date: new Date(current),
          count: existing ? existing.count : 0
        });
        current.setMonth(current.getMonth() + 1);
      }
      
      return months;
    }
    
    // For "This Month", ensure all weeks are included
    if (timeFilter === 'This Month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const weeks = [];
      let weekStart = new Date(monthStart);
      weekStart.setDate(monthStart.getDate() - monthStart.getDay()); // Start of first week
      weekStart.setHours(0, 0, 0, 0);
      const firstWeekStart = new Date(weekStart);
      let weekNum = 1;
      
      while (weekStart <= monthEnd) {
        const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
        const existing = sorted.find(s => {
          const sWeekStart = new Date(s.date);
          sWeekStart.setHours(0, 0, 0, 0);
          return sWeekStart.getTime() === weekStart.getTime();
        });
        
        if (weekStart >= monthStart || (weekStart < monthStart && weekStart.getDate() + 6 >= monthStart.getDate())) {
          weeks.push({
            period: `Week ${weekNum} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`,
            date: new Date(weekStart),
            count: existing ? existing.count : 0
          });
        }
        
        weekStart.setDate(weekStart.getDate() + 7);
        weekNum++;
      }
      
      return weeks;
    }
    
    return sorted;
  }, [filteredClients, timeFilter]);

  // Calculate stage distribution for Active tab
  const stageDistribution = useMemo(() => {
    const STAGES = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte'];
    const counts = STAGES.reduce((acc, s) => { acc[s] = 0; return acc; }, {});
    filteredClients.forEach(c => {
      const stage = c.program?.programName || c.program?.status || c.stage || 'Unknown';
      if (STAGES.includes(stage)) {
        counts[stage] = (counts[stage] || 0) + 1;
      }
    });
    return counts;
  }, [filteredClients]);

  // Calculate revenue metrics
  const revenueMetrics = useMemo(() => {
    let startingRevenue = 0;
    let currentRevenue = 0;
    let revenueGoal = 0;
    let startingCount = 0;
    let currentCount = 0;
    let goalCount = 0;

    filteredClients.forEach(c => {
      const startRev = c.metrics?.startingRevenue || 0;
      const currRev = c.metrics?.currentRevenue || 0;
      const goalRev = c.metrics?.revenueGoal || 0;
      
      if (startRev > 0) {
        startingRevenue += startRev;
        startingCount++;
      }
      if (currRev > 0) {
        currentRevenue += currRev;
        currentCount++;
      }
      if (goalRev > 0) {
        revenueGoal += goalRev;
        goalCount++;
      }
    });

    return {
      startingRevenue: startingRevenue,
      currentRevenue: currentRevenue,
      revenueGoal: revenueGoal,
      avgStartingRevenue: startingCount > 0 ? Math.round(startingRevenue / startingCount) : 0,
      avgCurrentRevenue: currentCount > 0 ? Math.round(currentRevenue / currentCount) : 0,
      avgRevenueGoal: goalCount > 0 ? Math.round(revenueGoal / goalCount) : 0,
      startingCount,
      currentCount,
      goalCount
    };
  }, [filteredClients]);

  // Calculate average stars for departments
  const avgStars = useMemo(() => {
    const departments = ['general', 'sales', 'operations', 'technology', 'finance', 'marketing', 'hr'];
    const stars = {};
    
    departments.forEach(dept => {
      const ratings = [];
      filteredClients.forEach(c => {
        const rating = c.departments?.[dept]?.rating;
        if (rating && typeof rating === 'number' && rating > 0) {
          ratings.push(rating);
        }
      });
      stars[dept] = ratings.length > 0 
        ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
        : 0;
    });

    return stars;
  }, [filteredClients]);

  // Calculate date metrics
  const dateMetrics = useMemo(() => {
    let totalDays = 0;
    let validClients = 0;
    const now = new Date();

    filteredClients.forEach(c => {
      const programStart = c.program?.startDate;
      const programEnd = c.program?.endDate;
      const start = programStart 
        ? (programStart instanceof Date ? programStart : new Date(programStart))
        : null;
      const end = programEnd 
        ? (programEnd instanceof Date ? programEnd : new Date(programEnd))
        : (c.status === 'Lost' || c.status === 'lost' ? null : now); // Use now for active clients
      
      if (start && start instanceof Date && !isNaN(start.getTime())) {
        const endDate = end && end instanceof Date && !isNaN(end.getTime()) ? end : now;
        const days = Math.round((endDate - start) / (1000 * 60 * 60 * 24));
        if (days >= 0) {
          totalDays += days;
          validClients++;
        }
      }
    });

    return {
      avgClientLength: validClients > 0 ? Math.round((totalDays / validClients) * 10) / 10 : 0,
      validClients
    };
  }, [filteredClients]);

  // Calculate hour metrics
  const hourMetrics = useMemo(() => {
    let totalHourPlans = 0;
    let totalHoursUsed = 0;
    let clientsWithHourData = 0;

    filteredClients.forEach(c => {
      const hourlyPlan = c.hourlyPlan || 0;
      const timeEntries = c.timeEntries || [];
      
      if (hourlyPlan > 0 || timeEntries.length > 0) {
        totalHourPlans += hourlyPlan;
        
        // Calculate total hours from time entries
        const hoursFromEntries = timeEntries.reduce((total, entry) => {
          const hours = entry.hours || 0;
          const minutes = entry.minutes || 0;
          return total + hours + (minutes / 60);
        }, 0);
        
        totalHoursUsed += hoursFromEntries;
        clientsWithHourData++;
      }
    });

    return {
      totalHourPlans,
      totalHoursUsed: Math.round(totalHoursUsed * 10) / 10,
      clientsWithHourData
    };
  }, [filteredClients]);

  // Get status counts for All tab
  const statusCounts = useMemo(() => {
    if (activeTab !== 'All') return { active: 0, lost: 0, total: 0 };
    
    let active = 0;
    let lost = 0;
    
    filteredClients.forEach(c => {
      const status = c.status || c.program?.clientStatus || c.stage;
      if (status === 'Lost' || status === 'lost') {
        lost++;
      } else {
        active++;
      }
    });

    return { active, lost, total: filteredClients.length };
  }, [filteredClients, activeTab]);

  // Calculate clients over time for line graph
  const clientsOverTime = useMemo(() => {
    if (!filteredClients.length && activeTab !== 'Lost') return [];
    const now = new Date();
    
    // Determine the start date and periods based on time filter
    let periodStart = new Date(0);
    let periods = [];
    
    if (timeFilter === 'All Time') {
      // Get all years from first client to now
      const years = new Set();
      filteredClients.forEach(c => {
        const created = c.createdAt instanceof Date ? c.createdAt : (c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt));
        if (created && created instanceof Date && !isNaN(created.getTime())) {
          years.add(created.getFullYear());
        }
      });
      const currentYear = now.getFullYear();
      years.add(currentYear);
      periods = Array.from(years).sort().map(year => ({
        key: `${year}`,
        label: year.toString(),
        date: new Date(year, 0, 1),
        endDate: new Date(year, 11, 31, 23, 59, 59)
      }));
    } else if (timeFilter === 'This Year') {
      const startDate = new Date(now.getFullYear(), 0, 1);
      const current = new Date(startDate);
      while (current <= now) {
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59);
        periods.push({
          key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
          label: current.toLocaleDateString('en-US', { month: 'short' }),
          date: monthStart,
          endDate: monthEnd
        });
        current.setMonth(current.getMonth() + 1);
      }
    } else if (timeFilter === 'This Quarter') {
      const q = Math.floor(now.getMonth() / 3);
      const startDate = new Date(now.getFullYear(), q * 3, 1);
      const current = new Date(startDate);
      while (current <= now) {
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59);
        periods.push({
          key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
          label: current.toLocaleDateString('en-US', { month: 'short' }),
          date: monthStart,
          endDate: monthEnd
        });
        current.setMonth(current.getMonth() + 1);
      }
    } else if (timeFilter === 'This Month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      let weekStart = new Date(monthStart);
      weekStart.setDate(monthStart.getDate() - monthStart.getDay());
      weekStart.setHours(0, 0, 0, 0);
      let weekNum = 1;
      const firstWeekStart = new Date(weekStart);
      
      while (weekStart <= monthEnd) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59);
        
        if (weekStart >= monthStart || (weekStart < monthStart && weekStart.getDate() + 6 >= monthStart.getDate())) {
          const weekNumCalc = Math.floor((weekStart - firstWeekStart) / (7 * 24 * 60 * 60 * 1000)) + 1;
          periods.push({
            key: `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`,
            label: `Week ${weekNumCalc} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`,
            date: new Date(weekStart),
            endDate: weekEnd
          });
        }
        weekStart.setDate(weekStart.getDate() + 7);
        weekNum++;
      }
    }
    
    // For Lost tab, count when clients were lost (existing logic)
    if (activeTab === 'Lost') {
      const periodCounts = {};
      filteredClients.forEach(c => {
        const programEnd = c.program?.endDate;
        const lostDate = programEnd 
          ? (programEnd instanceof Date ? programEnd : (programEnd === 'N/A' || programEnd === null || programEnd === undefined ? null : new Date(programEnd)))
          : (c.lostAt instanceof Date ? c.lostAt : (c.lostAt?.toDate ? c.lostAt.toDate() : null)) 
            || (c.lastActivityAt instanceof Date ? c.lastActivityAt : (c.lastActivityAt?.toDate ? c.lastActivityAt.toDate() : null));
        
        if (!lostDate || !(lostDate instanceof Date) || isNaN(lostDate.getTime())) return;
        
        // Find which period this belongs to
        const period = periods.find(p => lostDate >= p.date && lostDate <= p.endDate);
        if (period) {
          if (!periodCounts[period.key]) {
            periodCounts[period.key] = 0;
          }
          periodCounts[period.key]++;
        }
      });
      
      return periods.map(p => ({
        period: p.label,
        date: p.date,
        count: periodCounts[p.key] || 0
      }));
    }
    
    // For Active/All tabs, calculate cumulative active (non-lost) clients at each period
    // Use allClients for All tab to get all clients, filteredClients for Active tab
    const clientsToCheck = activeTab === 'All' ? allClients : filteredClients;
    
    return periods.map(period => {
      const count = clientsToCheck.filter(c => {
        const created = c.createdAt instanceof Date ? c.createdAt : (c.createdAt?.toDate ? c.createdAt.toDate() : new Date(c.createdAt));
        if (!created || !(created instanceof Date) || isNaN(created.getTime())) return false;
        
        // Client must have been created before or during this period
        if (created > period.endDate) return false;
        
        // Check if client was still active (non-lost) at the end of this period
        const status = c.status || c.program?.clientStatus || c.stage;
        const programEnd = c.program?.endDate;
        const endDate = programEnd 
          ? (programEnd instanceof Date ? programEnd : (programEnd === 'N/A' || programEnd === null || programEnd === undefined ? null : new Date(programEnd)))
          : null;
        
        const isLost = status === 'Lost' || status === 'lost';
        const hasNoEndDate = endDate === null || endDate === undefined || endDate === 'N/A';
        
        // Client is active at period end if:
        // 1. Not lost, OR
        // 2. Lost but end date is after period end (was still active during this period), OR
        // 3. Lost but no end date (treat as active if status allows)
        if (isLost) {
          if (hasNoEndDate) {
            // If lost but no end date, don't count as active
            return false;
          }
          if (endDate && endDate instanceof Date && !isNaN(endDate.getTime())) {
            // If lost with end date, only count if end date is after period end
            return endDate > period.endDate;
          }
          return false;
        }
        
        // Not lost - check if they have an end date that would exclude them
        if (!hasNoEndDate && endDate && endDate instanceof Date && !isNaN(endDate.getTime())) {
          // If end date exists and is before/at period end, they weren't active
          return endDate > period.endDate;
        }
        
        // Client is active
        return true;
      }).length;
      
      return {
        period: period.label,
        date: period.date,
        count
      };
    });
  }, [filteredClients, allClients, timeFilter, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>Loading clients…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/reports')} />
          <h1 className={`text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white`}>Clients Reports</h1>
          <div className="flex items-center space-x-2">
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className={`px-3 py-2 border rounded-md shadow-sm text-sm ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="All Time">All Time</option>
              <option value="This Year">This Year</option>
              <option value="This Quarter">This Quarter</option>
              <option value="This Month">This Month</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center space-x-4 mb-6 border-b border-gray-300 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('All')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'All'
                ? `${darkMode ? 'border-red-500 text-red-400' : 'border-red-600 text-red-600'}`
                : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('Active')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'Active'
                ? `${darkMode ? 'border-red-500 text-red-400' : 'border-red-600 text-red-600'}`
                : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setActiveTab('Lost')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${
              activeTab === 'Lost'
                ? `${darkMode ? 'border-red-500 text-red-400' : 'border-red-600 text-red-600'}`
                : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700'}`
            }`}
          >
            Lost
          </button>
        </div>

        {/* Summary Cards */}
        {activeTab === 'Lost' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Lost Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{totals.total}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Long Term (6 Months+) Lost Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{longTermLostClients}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Days As Client</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{avgDaysAsClient}</div>
              </div>
            </div>

            {/* Clients Over Time Chart */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
              <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Lost Clients Over Time</h2>
              <div className="h-96">
                {clientsOverTime.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No client data available</div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={clientsOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280', fontSize: 12 }}
                      />
                      <YAxis tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280' }} />
                      <Tooltip 
                        formatter={(value) => [`${value} clients`, 'Count']}
                        contentStyle={{
                          backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                          border: darkMode ? '1px solid #374151' : '1px solid #E5E7EB',
                          color: darkMode ? '#FFFFFF' : '#000000'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        name="Lost Clients" 
                        stroke="#EF4444" 
                        strokeWidth={2}
                        dot={{ fill: '#EF4444', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </>
        ) : activeTab === 'Active' ? (
          <>
            {/* Status Counts Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-green-500' : 'text-green-600'}`}>{totals.total}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>All Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{allClients.length}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Lost Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-red-500' : 'text-red-600'}`}>
                  {allClients.filter(c => {
                    const status = c.status || c.program?.clientStatus || c.stage;
                    return status === 'Lost' || status === 'lost';
                  }).length}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Client Length (Days)</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dateMetrics.avgClientLength}</div>
              </div>
            </div>

            {/* Clients Over Time Chart */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
              <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Active Clients Over Time</h2>
              <div className="h-96">
                {clientsOverTime.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No client data available</div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={clientsOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280', fontSize: 12 }}
                      />
                      <YAxis tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280' }} />
                      <Tooltip 
                        formatter={(value) => [`${value} clients`, 'Count']}
                        contentStyle={{
                          backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                          border: darkMode ? '1px solid #374151' : '1px solid #E5E7EB',
                          color: darkMode ? '#FFFFFF' : '#000000'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        name="Active Clients" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        dot={{ fill: '#10B981', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Revenue Totals Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Starting Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ${revenueMetrics.startingRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Current Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-green-500' : 'text-green-600'}`}>
                  ${revenueMetrics.currentRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Revenue Goal</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-blue-500' : 'text-blue-600'}`}>
                  ${revenueMetrics.revenueGoal.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Revenue Averages Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Starting Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ${revenueMetrics.avgStartingRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Current Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-green-500' : 'text-green-600'}`}>
                  ${revenueMetrics.avgCurrentRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Revenue Goal</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-blue-500' : 'text-blue-600'}`}>
                  ${revenueMetrics.avgRevenueGoal.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Average Stars Row */}
            <div className="mb-6">
              {/* First Row: General, Sales, Operations (1/3 width each) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {[
                  { name: 'General', key: 'general' },
                  { name: 'Sales', key: 'sales' },
                  { name: 'Operations', key: 'operations' }
                ].map(({ name, key }) => {
                  const rating = avgStars[key] || 0;
                  return (
                    <div key={key} className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-32 flex flex-col items-center justify-center text-center`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>{name}</div>
                      <div className="flex items-center justify-center space-x-1.5 mb-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <FaStar
                            key={star}
                            className={`w-6 h-6 ${
                              star <= Math.round(rating)
                                ? darkMode ? 'text-yellow-400' : 'text-yellow-500'
                                : darkMode ? 'text-gray-600' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      {rating > 0 && (
                        <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {rating.toFixed(1)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Second Row: Technology, Finance, Marketing, HR (1/4 width each) */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { name: 'Technology', key: 'technology' },
                  { name: 'Finance', key: 'finance' },
                  { name: 'Marketing', key: 'marketing' },
                  { name: 'HR', key: 'hr' }
                ].map(({ name, key }) => {
                  const rating = avgStars[key] || 0;
                  return (
                    <div key={key} className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-32 flex flex-col items-center justify-center text-center`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>{name}</div>
                      <div className="flex items-center justify-center space-x-1.5 mb-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <FaStar
                            key={star}
                            className={`w-6 h-6 ${
                              star <= Math.round(rating)
                                ? darkMode ? 'text-yellow-400' : 'text-yellow-500'
                                : darkMode ? 'text-gray-600' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      {rating > 0 && (
                        <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {rating.toFixed(1)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stage Distribution Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
              <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Stage Distribution</h3>
              <div className="h-96">
                {(() => {
                  const stageData = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte']
                    .map(stage => ({
                      stage,
                      count: stageDistribution[stage] || 0
                    }))
                    .filter(item => item.count > 0);
                  
                  if (stageData.length === 0) {
                    return (
                      <div className="flex items-center justify-center h-full">
                        <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No stage data available</div>
                      </div>
                    );
                  }
                  
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="stage" />
                        <YAxis />
                        <Tooltip formatter={(v) => [`${v} clients`, 'Count']} />
                        <Legend />
                        <Bar dataKey="count" name="Count" fill="#EF4444">
                          {stageData.map((entry, idx) => (
                            <Cell key={`stage-${idx}`} fill={colors[idx % colors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>

            {/* Date and Hours Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Hour Plans</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{hourMetrics.totalHourPlans}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Hours Used</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{hourMetrics.totalHoursUsed}</div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Status Counts Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{totals.total}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-green-500' : 'text-green-600'}`}>{statusCounts.active}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Lost Clients</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-red-500' : 'text-red-600'}`}>{statusCounts.lost}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Client Length (Days)</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{dateMetrics.avgClientLength}</div>
              </div>
            </div>

            {/* Clients Over Time Chart */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
              <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Clients Over Time</h2>
              <div className="h-96">
                {clientsOverTime.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No client data available</div>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={clientsOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="period" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280', fontSize: 12 }}
                      />
                      <YAxis tick={{ fill: darkMode ? '#9CA3AF' : '#6B7280' }} />
                      <Tooltip 
                        formatter={(value) => [`${value} clients`, 'Count']}
                        contentStyle={{
                          backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                          border: darkMode ? '1px solid #374151' : '1px solid #E5E7EB',
                          color: darkMode ? '#FFFFFF' : '#000000'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        name="Clients" 
                        stroke="#3B82F6" 
                        strokeWidth={2}
                        dot={{ fill: '#3B82F6', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Revenue Totals Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Starting Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ${revenueMetrics.startingRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Current Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-green-500' : 'text-green-600'}`}>
                  ${revenueMetrics.currentRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Revenue Goal</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-blue-500' : 'text-blue-600'}`}>
                  ${revenueMetrics.revenueGoal.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Revenue Averages Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Starting Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  ${revenueMetrics.avgStartingRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Current Revenue</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-green-500' : 'text-green-600'}`}>
                  ${revenueMetrics.avgCurrentRevenue.toLocaleString()}
                </div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Avg Revenue Goal</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-blue-500' : 'text-blue-600'}`}>
                  ${revenueMetrics.avgRevenueGoal.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Average Stars Row */}
            <div className="mb-6">
              {/* First Row: General, Sales, Operations (1/3 width each) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {[
                  { name: 'General', key: 'general' },
                  { name: 'Sales', key: 'sales' },
                  { name: 'Operations', key: 'operations' }
                ].map(({ name, key }) => {
                  const rating = avgStars[key] || 0;
                  return (
                    <div key={key} className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-32 flex flex-col items-center justify-center text-center`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>{name}</div>
                      <div className="flex items-center justify-center space-x-1.5 mb-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <FaStar
                            key={star}
                            className={`w-6 h-6 ${
                              star <= Math.round(rating)
                                ? darkMode ? 'text-yellow-400' : 'text-yellow-500'
                                : darkMode ? 'text-gray-600' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      {rating > 0 && (
                        <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {rating.toFixed(1)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Second Row: Technology, Finance, Marketing, HR (1/4 width each) */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { name: 'Technology', key: 'technology' },
                  { name: 'Finance', key: 'finance' },
                  { name: 'Marketing', key: 'marketing' },
                  { name: 'HR', key: 'hr' }
                ].map(({ name, key }) => {
                  const rating = avgStars[key] || 0;
                  return (
                    <div key={key} className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-32 flex flex-col items-center justify-center text-center`}>
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>{name}</div>
                      <div className="flex items-center justify-center space-x-1.5 mb-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <FaStar
                            key={star}
                            className={`w-6 h-6 ${
                              star <= Math.round(rating)
                                ? darkMode ? 'text-yellow-400' : 'text-yellow-500'
                                : darkMode ? 'text-gray-600' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      {rating > 0 && (
                        <div className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {rating.toFixed(1)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stage Distribution Card */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
              <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Stage Distribution</h3>
              <div className="h-96">
                {(() => {
                  const stageData = ['Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte']
                    .map(stage => ({
                      stage,
                      count: stageDistribution[stage] || 0
                    }))
                    .filter(item => item.count > 0);
                  
                  if (stageData.length === 0) {
                    return (
                      <div className="flex items-center justify-center h-full">
                        <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No stage data available</div>
                      </div>
                    );
                  }
                  
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="stage" />
                        <YAxis />
                        <Tooltip formatter={(v) => [`${v} clients`, 'Count']} />
                        <Legend />
                        <Bar dataKey="count" name="Count" fill="#EF4444">
                          {stageData.map((entry, idx) => (
                            <Cell key={`stage-${idx}`} fill={colors[idx % colors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>

            {/* Date and Hours Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Hour Plans</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{hourMetrics.totalHourPlans}</div>
              </div>
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 h-28 flex flex-col items-center justify-center text-center`}>
                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Hours Used</div>
                <div className={`text-3xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{hourMetrics.totalHoursUsed}</div>
              </div>
            </div>
          </>
        )}

        {/* Charts Section - Only show for Lost tab */}
        {activeTab === 'Lost' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
                <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Reasons Lost</h2>
                <div className="h-96">
                  {lostByReason.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No lost clients recorded</div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={lostByReason} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="reason" type="category" width={160} />
                        <Tooltip formatter={(v) => [`${v} clients`, 'Count']} />
                        <Legend />
                        <Bar dataKey="count" name="Count">
                          {lostByReason.map((entry, idx) => (
                            <Cell key={`r-${idx}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
                <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Program Stage When Lost</h2>
                <div className="h-96">
                  {lostByStage.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No stage data available</div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={lostByStage} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="stage" type="category" width={160} />
                        <Tooltip formatter={(v) => [`${v} clients`, 'Count']} />
                        <Legend />
                        <Bar dataKey="count" name="Count">
                          {lostByStage.map((entry, idx) => (
                            <Cell key={`s-${idx}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {activeTab === 'Lost' ? 'Lost Clients' : activeTab === 'Active' ? 'Active Clients' : 'All Clients'}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <th className="text-left pb-2 pr-4">Company Name</th>
                  <th className="text-left pb-2 pr-4">Contact Name</th>
                  {activeTab === 'Lost' ? (
                    <>
                      <th className="text-left pb-2 pr-4">Reason</th>
                      <th className="text-left pb-2 pr-4">Program Lost</th>
                      <th className="text-left pb-2 pr-4">Start Date</th>
                      <th className="text-left pb-2">End Date</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left pb-2 pr-4">Status</th>
                      <th className="text-left pb-2 pr-4">Program</th>
                      <th className="text-left pb-2">Created</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className={`${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                {filteredClients.slice(0, 25).map((c) => {
                  // Get contact name from nested contact object or fallback fields
                  const contactFirst = c.contact?.firstName || '';
                  const contactLast = c.contact?.lastName || '';
                  const contactName = (contactFirst && contactLast) 
                    ? `${contactFirst} ${contactLast}`.trim()
                    : c.contactName || c.primaryContactName || c.contact?.name || c.name || '-';
                  
                  // Get status
                  const status = c.status || c.program?.clientStatus || c.stage || 'Unknown';
                  
                  // Get program name
                  const programName = c.program?.programName || c.program?.status || '-';
                  
                  if (activeTab === 'Lost') {
                    const reason = c.resultReason || c.substatus || c.reason || c.lostReason || 'Unknown';
                    const prevStage = c.program?.programName
                      || c.program?.status
                      || c.lastVisitedStage
                      || c.previousStage
                      || (Array.isArray(c.stageHistory) && c.stageHistory.length > 1 ? c.stageHistory[c.stageHistory.length - 2]?.stage : null)
                      || c.stageBeforeLost
                      || c.pipelineStage
                      || 'Unknown';
                    // Get start and end dates from program overview
                    const programStart = c.program?.startDate;
                    const programEnd = c.program?.endDate;
                    const startDate = programStart 
                      ? (programStart instanceof Date 
                          ? programStart.toLocaleDateString() 
                          : new Date(programStart).toLocaleDateString())
                      : '-';
                    const endDate = programEnd 
                      ? (programEnd instanceof Date 
                          ? programEnd.toLocaleDateString() 
                          : new Date(programEnd).toLocaleDateString())
                      : '-';
                    return (
                      <tr key={c.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer" onClick={() => navigate(`/crm/client/${c.id}`)}>
                        <td className="py-2 pr-4">{c.company || c.accountName || c.businessName || c.organization || c.name || c.email || c.phone || c.id}</td>
                        <td className="py-2 pr-4">{contactName}</td>
                        <td className="py-2 pr-4">{reason}</td>
                        <td className="py-2 pr-4">{prevStage}</td>
                        <td className="py-2 pr-4">{startDate}</td>
                        <td className="py-2">{endDate}</td>
                      </tr>
                    );
                  } else {
                    const created = c.createdAt instanceof Date ? c.createdAt.toLocaleDateString() : '-';
                    return (
                      <tr key={c.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer" onClick={() => navigate(`/crm/client/${c.id}`)}>
                        <td className="py-2 pr-4">{c.company || c.accountName || c.businessName || c.organization || c.name || c.email || c.phone || c.id}</td>
                        <td className="py-2 pr-4">{contactName}</td>
                        <td className="py-2 pr-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            status === 'Lost' || status === 'lost'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {status}
                          </span>
                        </td>
                        <td className="py-2 pr-4">{programName}</td>
                        <td className="py-2">{created}</td>
                      </tr>
                    );
                  }
                })}
                {filteredClients.length === 0 && (
                  <tr>
                    <td colSpan={activeTab === 'Lost' ? 6 : 5} className={`py-6 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No {activeTab === 'Lost' ? 'lost ' : activeTab === 'Active' ? 'active ' : ''}clients found for selected period
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {editOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => !editSaving && setEditOpen(false)} />
              <div className={`bg-white dark:bg-gray-900 text-gray-900 dark:text-white relative z-10 w-full max-w-lg rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl p-6`}>
                <div className="mb-4 text-center">
                  <h3 className="text-lg font-semibold">Edit Lost Client</h3>
                </div>
                <div className="space-y-4">
                  {/* Details overview */}
                  <div className={`bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4`}>
                    {editClient && (() => {
                      const created = editClient.createdAt instanceof Date ? editClient.createdAt.toLocaleDateString() : '-';
                      const lost = editClient.lostAt instanceof Date ? editClient.lostAt.toLocaleDateString() : '-';
                      const lastAct = editClient.lastActivityAt instanceof Date ? editClient.lastActivityAt.toLocaleDateString() : '-';
                      const start = editClient.createdAt || editClient.firstSeenAt || editClient.firstContactAt || editClient.clientSince;
                      const end = editClient.lostAt || editClient.lastActivityAt || new Date();
                      const days = (start instanceof Date && end instanceof Date)
                        ? Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)))
                        : '-';
                      const createdBy = editClient.createdByName || editClient.createdBy || editClient.ownerName || editClient.owner || editClient.ownerId || '-';
                      const displaySource = editClient.source || editClient.channel || editClient.campaign || '-';
                      return (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Days As Client</div>
                            <div className="font-medium">{days}</div>
                          </div>
                          <div>
                            <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Created</div>
                            <div className="font-medium">{created}</div>
                          </div>
                          <div>
                            <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Lost</div>
                            <div className="font-medium">{lost}</div>
                          </div>
                          <div>
                            <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Last Activity</div>
                            <div className="font-medium">{lastAct}</div>
                          </div>
                          <div className="col-span-2">
                            <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Created By</div>
                            <div className="font-medium">{createdBy}</div>
                          </div>
                          <div className="col-span-2">
                            <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Source</div>
                            <div className="font-medium">{displaySource}</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Company Name</label>
                      <input
                        type="text"
                        value={editCompanyName}
                        onChange={(e) => setEditCompanyName(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        placeholder="Acme Co."
                        disabled={editSaving}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Contact Name</label>
                      <input
                        type="text"
                        value={editContactName}
                        onChange={(e) => setEditContactName(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        placeholder="Jane Doe"
                        disabled={editSaving}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Email</label>
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        placeholder="jane@example.com"
                        disabled={editSaving}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone</label>
                      <input
                        type="tel"
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        placeholder="(555) 555-5555"
                        disabled={editSaving}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reason</label>
                    <input
                      type="text"
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="e.g., Budget constraints"
                      disabled={editSaving}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Source</label>
                    <input
                      type="text"
                      value={editSource}
                      onChange={(e) => setEditSource(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="e.g., Website, Referral, Facebook"
                      disabled={editSaving}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Program Stage When Lost</label>
                    <select
                      value={editStageBeforeLost}
                      onChange={(e) => setEditStageBeforeLost(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      disabled={editSaving}
                    >
                      <option value="">Select program</option>
                      <option value="Lite">Lite</option>
                      <option value="Plus">Plus</option>
                      <option value="Pro">Pro</option>
                      <option value="Hourly">Hourly</option>
                      <option value="5 Hours">5 Hours</option>
                      <option value="10 Hours">10 Hours</option>
                      <option value="A La Carte">A La Carte</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Notes</label>
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Add context"
                      disabled={editSaving}
                    />
                  </div>
                  {editError && (
                    <div className="text-red-500 text-sm">{editError}</div>
                  )}
                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => setEditPostMortem(prev => !prev)}
                        disabled={editSaving}
                        className={`px-4 py-2 rounded-md border mr-2 ${
                          editPostMortem
                            ? `${darkMode ? 'bg-green-500 border-green-600 text-white' : 'bg-green-600 border-green-700 text-white'}`
                            : `${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-200 border-gray-300 text-gray-800'}`
                        } ${editSaving ? 'opacity-70' : ''}`}
                      >
                        Post Mortem
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                    <button
                      className={`px-4 py-2 rounded-md border ${darkMode ? 'border-gray-500 text-gray-200' : 'border-gray-300 text-gray-700'}`}
                      onClick={() => setEditOpen(false)}
                      disabled={editSaving}
                    >
                      Cancel
                    </button>
                    <button
                      className={`px-4 py-2 rounded-md ${editSaving ? 'opacity-70' : ''} ${darkMode ? 'bg-red-500 text-white' : 'bg-red-600 text-white'}`}
                      onClick={saveEdit}
                      disabled={editSaving}
                    >
                      {editSaving ? 'Saving…' : 'Save'}
                    </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LostClientsReports;

